"use client";
import React, { memo } from "react";
import { Box, TabProps, Typography } from "@mui/material";
import { SuccessIcon } from "@/icons";
import { AppTab, AppTabs } from "@/common";

const AppTabStepperActions = ({
  stepList = [],
  value,
  isEditPage = false,
  onChangeStep,
}: AppTabStepperActionsProps) => {
  const checkStepComponent = ({ active, completed }: StepIconCheckProps) => {
    if (completed) {
      return (
        <Box
          bgcolor="white"
          sx={{
            width: 16,
            height: 16,
            borderRadius: "50%",
          }}
        >
          <SuccessIcon
            sx={{
              fontSize: "16px",
              color: active || completed ? "primary.main" : "#DEE1E6FF",
            }}
          />
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            backgroundColor: "common.white",
            border: "2px solid",
            borderColor: active ? "primary.main" : "#BDC1CAFF",
          }}
        />
      );
    }
  };

  return (
    <Box
      className="center-root"
      sx={{
        width: "100%",
        pb: 2,
        pt: 1,
        backgroundColor: "common.white",
        px: 8,
      }}
      id="panel-tab"
    >
      <Box position="relative">
        <AppTabs
          value={value}
          sx={{
            width: "fit-content",
            "&,.MuiTabs-scroller": {
              overflow: "visible !important",
            },

            ".MuiTabs-flexContainer": {
              "&:after": {
                content: "''",
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                width: "100%",
                height: 6,
                backgroundColor: "#DEE1E6FF",
              },
              "&:before": {
                content: "''",
                position: "absolute",
                bottom: 0,
                right: 0,
                width: 16,
                height: 16,
                display: "block",
                borderRadius: "50%",
              },
            },
            "& .MuiTabs-indicator": {
              height: 6,
              left: "0 !important",
              width: `${WITH_TAB * value}px !important`,
            },
          }}
          onChange={(_, value) => {
            if (!isEditPage) return;

            onChangeStep?.(value);
          }}
        >
          {stepList.map((item) => {
            const isCompleted = item.id < value;
            return (
              <StepCustom
                type={isEditPage ? "submit" : "button"}
                key={item.id}
                label={
                  <Typography
                    className="ellipsis"
                    width="100%"
                    textAlign="left"
                    color={isCompleted ? "primary.main" : "inherit"}
                    fontSize={18}
                    lineHeight="24px"
                    ml={-1}
                  >
                    <Typography component="span" fontSize={18} fontWeight={700}>
                      0{item.id}
                    </Typography>
                    {" " + item.label}
                  </Typography>
                }
                value={item.id}
                sx={{
                  cursor: isEditPage ? "pointer" : "default",
                  pl: 0,
                  minWidth: WITH_TAB,
                  width: WITH_TAB,
                  alignItems: "flex-start",
                  py: 1.5,
                  overflow: "visible",
                  pt: 0,
                  "& .MuiTab-iconWrapper": {
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    height: 16,
                    mb: "3px",
                    transform: "translate(-50%,50%)",
                    zIndex: 1,
                  },
                }}
                icon={checkStepComponent({
                  active: value === item.id,
                  completed: isCompleted,
                })}
              />
            );
          })}
        </AppTabs>
        <Box
          sx={{
            position: "absolute",
            bgcolor: "common.white",
            bottom: 0,
            height: 16,
            right: 0,
            transform: "translate(50%,50%)",
            borderRadius: "50%",
            mb: "3px",
          }}
        >
          <SuccessIcon
            sx={{
              fontSize: "16px",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const WITH_TAB = 200;

type AppTabStepperActionsProps = {
  stepList: Array<{ id: number; label: string }>;
  value: number;
  isEditPage?: boolean;
  onChangeStep?: (value: number) => void;
};

const StepCustom = (
  props: TabProps & {
    type: "submit" | "button";
  }
) => {
  return <AppTab {...props} />;
};

export default memo(AppTabStepperActions);

type StepIconCheckProps = {
  active: boolean;
  completed: boolean;
};
